import { useStorefrontMiniBanners } from '@/hooks/useStorefrontMiniBanners';
import { useStorefrontTheme } from '@/contexts/StorefrontThemeContext';
import { cn } from '@/lib/utils';
import BannerLinkWrapper, { hasBannerLink, type BannerLinkContext } from '@/components/storefront-themes/eletronicos/BannerLinkWrapper';
import { BannerPlaceholder } from '@/components/storefront-themes/eletronicos/BannerPlaceholder';

interface MiniBannerGridProps {
  userId: string;
  linkContext: BannerLinkContext;
}

const PLACEHOLDER_SLOTS = 3;

export default function MiniBannerGrid({ userId, linkContext }: MiniBannerGridProps) {
  const { appearance } = useStorefrontTheme();
  const { banners, loading } = useStorefrontMiniBanners(userId, { activeOnly: true });

  // Only when the merchant has left the section on but hasn't uploaded any mini
  // banner yet — turning the section off (mini_banners_enabled) still hides it,
  // that's a deliberate choice, not "not filled in yet".
  if (loading || !appearance.mini_banners_enabled) return null;

  if (banners.length === 0) {
    return (
      <div>
        <div className="container mx-auto px-4 py-8 md:py-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {Array.from({ length: PLACEHOLDER_SLOTS }).map((_, i) => (
              <BannerPlaceholder key={i} className="w-full aspect-[832/960]" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: appearance.mini_banners_bg_color }}>
      <div className="container mx-auto px-4 py-8 md:py-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {banners.map((banner) => {
            const clickable = hasBannerLink(banner.link_url);
            return (
              <BannerLinkWrapper
                key={banner.id}
                link={banner.link_url}
                context={linkContext}
                className={cn(
                  'group block overflow-hidden transition-all duration-300 motion-reduce:transition-none',
                  'hover:shadow-lg hover:-translate-y-0.5 motion-reduce:hover:translate-y-0',
                  clickable && 'cursor-pointer active:translate-y-0 active:scale-[0.99]'
                )}
              >
                <img
                  src={banner.image_url}
                  alt=""
                  loading="lazy"
                  className="w-full aspect-[832/960] object-cover transition-transform duration-500 ease-out group-hover:scale-105 motion-reduce:transition-none motion-reduce:group-hover:scale-100"
                />
              </BannerLinkWrapper>
            );
          })}
        </div>
      </div>
    </div>
  );
}
